import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, LoaderCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import './agreements.css';

export const Action = ({ children, secondary = false, ...props }) => <Button type="button" className={`btn ${secondary ? 'btn-outline' : 'btn-primary'} ag-action`} {...props}>{children}</Button>;
export const Notice = ({ children, error = false, id = 'agreement-notice' }) => children ? <div role={error ? 'alert' : 'status'} data-testid={id} className={`alert ${error ? 'alert-error' : 'alert-success'} ag-notice`}>{children}</div> : null;
export const AgreementShell = ({ title, eyebrow, back = '/admin/agreements', children, publicView = false }) => <div className="agreement-area">
  <header className="ag-header"><div className="ag-header-inner">
    <img src="/logo.webp" alt="ST Books" className="ag-logo" data-testid="agreement-header-logo" />
    <div><strong data-testid="agreement-company-name">ST Books Pty Ltd</strong><span data-testid="agreement-company-abn">ABN 75 669 741 332</span></div>
    {!publicView && <Link to={back} className="ag-back" data-testid="agreement-back-link"><ArrowLeft size={18} /> Back</Link>}
  </div></header>
  <main className="ag-main"><div className="ag-page-heading"><p className="ag-eyebrow">{eyebrow}</p><h1 data-testid="agreement-page-title">{title}</h1></div>{children}</main>
</div>;

export const StatusBadge = ({ status, id }) => <span className={`ag-status ag-status-${status}`} data-testid={id}>{status?.replaceAll('_', ' ')}</span>;

export function usePdfViewer() {
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => () => { if (pdf?.url) URL.revokeObjectURL(pdf.url); }, [pdf]);
  const open = async (fetcher, title) => {
    setError(''); setLoading(true);
    try { const response = await fetcher(); setPdf({ url: URL.createObjectURL(response.data), title }); }
    catch (e) {
      try { const detail = JSON.parse(await e.response.data.text()).detail; setError(typeof detail === 'string' ? detail : 'Unable to open this PDF.'); }
      catch (_) { setError('Unable to open this PDF. Please try again.'); }
    } finally { setLoading(false); }
  };
  const viewer = <>
    {loading && <p className="ag-loading" data-testid="pdf-loading"><LoaderCircle className="ag-spin" size={18} /> Opening document…</p>}
    <Notice error id="pdf-error">{error}</Notice>
    <Dialog open={Boolean(pdf)} onOpenChange={open => !open && setPdf(null)}>
      <DialogContent className="ag-pdf-dialog" data-testid="pdf-viewer-modal">
        <DialogTitle>{pdf?.title}</DialogTitle><DialogDescription className="sr-only">Agreement document</DialogDescription>
        {pdf && <iframe title={pdf.title} src={pdf.url} data-testid="pdf-preview-frame" />}
        <div className="ag-actions"><a href={pdf?.url} download={`${pdf?.title || 'Document'}.pdf`} className="btn btn-primary" data-testid="pdf-download-link"><Download size={16} /> Download PDF</a><Action secondary data-testid="pdf-close-button" onClick={() => setPdf(null)}><X size={16} /> Close</Action></div>
      </DialogContent>
    </Dialog>
  </>;
  return { open, viewer, loading };
}

export const ConfirmAction = ({ pending, busy, onClose, onConfirm }) => <Dialog open={Boolean(pending)} onOpenChange={value => !value && !busy && onClose()}>
  <DialogContent className="agreement-area ag-confirm-dialog" data-testid="agreement-confirm-modal"><DialogTitle>{pending?.title}</DialogTitle><DialogDescription>{pending?.description}</DialogDescription>
    <div className="ag-actions"><Action secondary disabled={busy} data-testid="agreement-confirm-back" onClick={onClose}>Go back</Action><Action disabled={busy} data-testid="agreement-confirm-submit" onClick={onConfirm}>{busy ? 'Working…' : 'Confirm'}</Action></div>
  </DialogContent>
</Dialog>;